import { useState, useEffect, useCallback } from 'react';
import { PAGE_HTML } from '../data/pageHtml';
import { loadData, saveToCode } from './codeDataStore';

export const DEFAULT_PAGE_CONTENTS = {
  about_us: {
    overview: '',
    mission: 'To publish high-quality, peer-reviewed clinical, pharmacological, and philosophical scientific research that contributes to the advancement of homoeopathy, patient safety, and evidence-informed integrative healthcare globally.',
    mission_bullets: [
      'Publish rigorous original clinical trials, observational studies, and verified case reports.',
      'Uphold the highest standards of international double-blind peer review and COPE publication ethics.',
      'Provide open access medical research to academicians, clinicians, and health policy researchers.'
    ],
    vision: 'To become a premier international homoeopathic journal indexed in leading global biomedical databases, bridging classical Hahnemannian principles and modern biomedical research methodologies.',
    scope: 'The scope includes clinical homeopathy, repertory research, homoeopathic materia medica, ultra-high dilution pharmacology, patient-reported outcome measures, and integrative clinical trials.',
    editorial_policy: 'All manuscripts are assessed solely on scientific rigor, clinical relevance, and ethical compliance. Editorial decisions are independent of political, commercial, or institutional influence.',
    who_can_submit: 'Homoeopathic practitioners, postgraduate scholars, academicians, pharmacologists, integrative medicine researchers, and healthcare professionals from India and across the world are warmly invited to submit manuscripts.',
    journal_title: 'AIM Journal of Homoeopathic Medicine (AJHM)',
    journal_frequency: 'Biannual (January–June, July–December)',
    journal_model: 'Open Access, Peer-Reviewed',
    journal_history: 'Established 2025 by Domus Publications',
    publisher_name: 'Domus Publications, an academic publishing imprint of Domus Mediverse Private Limited.',
    publisher_address: 'Hill View Colony, Near Chandra Vatika, Khatima, Udham Singh Nagar - 262308, Uttarakhand, India.',
    publisher_affiliation: 'In scholarly collaboration with academic faculties and homoeopathic medical colleges advancing scientific homoeopathic education.'
  },

  aims_and_scope: {
    overview: '',
    aim: 'To provide an authoritative, peer-reviewed, open-access international platform dedicated to publishing high-quality, methodologically sound scientific research in homoeopathic medicine and allied integrative healthcare disciplines.',
    scope_items: [
      'Clinical research and clinical trials in homoeopathy',
      'Basic and applied biomedical investigations into ultra-dilutions',
      'Homoeopathic materia medica and pharmacological validations',
      'Organon of medicine and historical philosophical methodologies',
      'Evidence-based case reports and clinical case series (CARE guidelines)',
      'Public health, preventative healthcare, and community epidemiology',
      'Integrative and complementary clinical healthcare models',
      'Educational paradigms and pedagogical advances in homoeopathy',
      'Biostatistical methodology and evidence-based medicine standards'
    ],
    geographical_scope: 'Global in reach and international in authorship. While anchored in India—the global epicenter of homoeopathic healthcare—the journal invites contributions from all continents.',
    target_audience: 'A diverse international readership across healthcare, academic, and clinical environments including clinicians, postgraduate scholars, pharmacological researchers, and healthcare policy makers.',
    target_audience_bullets: [
      'Registered homoeopathic physicians, clinicians, and hospital consultants',
      'Postgraduate scholars, academic faculty, and medical researchers',
      'Pharmacologists and pharmaceutical research scientists studying high dilutions',
      'Public health specialists, epidemiologists, and integrative healthcare practitioners'
    ],
    editorial_scope: 'Every submitted manuscript undergoes rigorous double-blind peer review by qualified external subject experts. Submissions are judged strictly on clinical validity, methodological soundness, ethical adherence, and originality.',
    article_types: [
      { title: 'Original Research Articles', desc: 'Controlled clinical trials, observational studies, and basic laboratory investigations (3,000–5,000 words).' },
      { title: 'Clinical Case Reports', desc: 'Rigorous case reports adhering strictly to CARE guidelines with long-term follow-up (1,500–3,000 words).' },
      { title: 'Systematic Reviews & Meta-Analyses', desc: 'Critical comprehensive syntheses of published clinical and preclinical literature (4,000–6,000 words).' },
      { title: 'Short Communications & Letters', desc: 'Preliminary findings, technical notes, or scholarly letters to the editor on published papers (1,000–2,000 words).' }
    ]
  },

  peer_review: {
    overview: '',
    stages: [
      {
        step: '01',
        title: 'Initial Editorial Screening & Double-Blind Setup',
        timeframe: 'Day 1 – 5',
        desc: 'The Editor-in-Chief and Managing Editor examine the manuscript for scope, basic formatting, and originality (plagiarism check). Author identities are strictly blinded before sending to referees.'
      },
      {
        step: '02',
        title: 'Reviewer Assignment & Timeline',
        timeframe: 'Day 6 – 10',
        desc: 'A minimum of two independent, external subject-matter experts with demonstrated expertise in homoeopathy or clinical research are invited to evaluate the blinded paper.'
      },
      {
        step: '03',
        title: 'Independent Evaluation Period',
        timeframe: 'Day 11 – 25',
        desc: 'Reviewers critically assess the study design, methodology, homoeopathic prescribing rationale, statistical analysis, clinical implications, and clarity against international standards.'
      },
      {
        step: '04',
        title: 'Editorial Decision & Recommendation',
        timeframe: 'Day 26 – 35',
        desc: 'Based on reviewer evaluations, the editor reaches one of four decisions: (1) Accept; (2) Minor revisions; (3) Major revisions; (4) Reject. Anonymous feedback is shared with the corresponding author.'
      },
      {
        step: '05',
        title: 'Final Acceptance & Publication',
        timeframe: 'Day 36 – 45',
        desc: 'Upon satisfactory completion of requested revisions, the Editor-in-Chief issues formal acceptance followed by proof composition, DOI assignment, and Open Access publication.'
      }
    ],
    revision_text: 'When revisions are requested, authors are provided with an itemized editorial letter and detailed, anonymous reviewer reports.',
    revision_bullets: [
      'Revised Manuscript (Clean & Tracked Changes): Clear highlight of every modification made.',
      'Point-by-Point Rebuttal / Response Letter: Directly answering every referee and editor comment with corresponding page/line indicators.',
      'Turnaround Timelines: Minor revisions within 14 days; major revisions within 28 days.'
    ],
    confidentiality_text: 'Editors and reviewers treat all submitted manuscripts as privileged, strictly confidential material. Reviewers may not share manuscripts, use data or findings prior to publication, or disclose their peer evaluation without express written authorization from the Editor-in-Chief.',
    conflicts_text: 'Invited reviewers who have recent collaborative, institutional, financial, or competitive relationships with the authors must decline the evaluation invitation immediately. Reviewers must notify the editorial office whenever any circumstance might reasonably be seen to bias their judgment.',
    independence_text: 'Editorial decisions are made strictly on scientific validity, originality, and medical importance, completely independent of commercial interests, APC payment status, or the publisher.'
  },

  publication_ethics: {
    overview: '',
    conflict_of_interest: 'All authors, reviewers, and editors must declare any direct or indirect financial, institutional, personal, or commercial interests that could be perceived to influence their work or decisions. Disclosure statements must be included in the manuscript before peer review.',
    research_ethics: 'Clinical studies involving human participants must have received documented ethical approval from an institutional ethics committee (IEC/IRB) and written informed consent from participants or legal guardians. Studies involving animal subjects must comply with national animal care guidelines.',
    originality: 'Submitted manuscripts must represent original, unpublished work not under concurrent evaluation by any other journal or conference proceedings. Simultaneous submission of identical or substantially overlapping manuscripts is an explicit ethical violation.',
    plagiarism: 'AJHM enforces zero tolerance for plagiarism, duplicate publication, text recycling, data falsification, and image manipulation. All submissions undergo automated cross-checking against international similarity databases upon receipt.',
    corrections: 'When an error or omission in a published article is identified that does not compromise overall scientific conclusions, an Erratum or Corrigendum is published with a permanent DOI and linked to the original article.',
    retractions: 'Articles found to contain fraudulent data, unresolvable ethical violations, severe honest errors invalidating findings, or major copyright infringement will be formally retracted in strict compliance with COPE Retraction Guidelines.'
  },

  author_guidelines: {
    overview: '',
    manuscript_preparation: 'Manuscripts must present sound clinical or experimental data. Authors are encouraged to prepare their text in standard Microsoft Word (.docx) format with continuous line numbering.',
    formatting_font: 'Times New Roman or Calibri, 12 pt size.',
    formatting_spacing: '1.5 line spacing with 1 inch (2.54 cm) margins on all sides.',
    formatting_remedies: 'Italicize remedy names on first mention; use official HPI nomenclature.',
    structure: 'Original articles must follow standard structure: Title Page (separate file), Blinded Abstract & Keywords (250 words max), Introduction, Materials & Methods, Results, Discussion, Conclusion, and Vancouver-style References.',
    conflict_of_interest: 'All authors must declare any commercial, pharmaceutical, financial, or personal relationships that could inappropriately influence their work. If none, state: "The authors declare no conflict of interest."',
    funding: 'Disclose all financial support, including government, institutional, or university research grant numbers.',
    originality: 'Manuscripts must be original and not submitted simultaneously elsewhere. Submissions are checked using automated similarity software (max acceptable similarity < 10%).',
    ethics_and_consent: 'Human studies must provide Institutional Ethics Committee (IEC/IRB) approval letter. Clinical case reports require signed informed written patient consent before consideration.',
    tables_figures: 'Figures must be min 300 DPI resolution. The official language is clear, grammatical British or American English consistently maintained.',
    author_responsibility: 'All listed co-authors must have made substantive scientific contributions to study design, data collection, or critical manuscript revision.',
    submission_process: 'All submissions must be uploaded through the AJHM Online Manuscript Portal. Ensure the following mandatory documents are prepared before starting: Blinded Main Manuscript (DOCX), Title Page with Authors & Affiliations, Ethics Approval / Patient Consent, High-Resolution Figures.',
    apc_overview: 'To support open-access dissemination without barrier to readers, AJHM operates with an Article Processing Charge (APC) applicable only upon formal peer-review acceptance. There is no submission fee.',
    apc_fee: 'INR 3,500 (Indian Authors) / USD 100 (International Authors)',
    waiver_policy: 'Full or partial waivers are considered on a case-by-case basis for postgraduate research scholars and authors from low-income economies.'
  },

  contact: {
    overview: '',
    journal_email: 'editor@ajhm.org, submit@ajhm.org',
    journal_phone: '+91 94123 45678, +91 5943 250000',
    journal_address: 'AIM Homoeopathic Medical College & Hospital Campus, Editorial Office, Khatima, Udham Singh Nagar - 262308, Uttarakhand, India.',
    office_hours: 'Monday – Saturday: 9:00 AM – 5:00 PM IST (Closed Sundays & National Holidays)',
    publisher_name: 'Domus Publications, an academic publishing imprint of Domus Mediverse Private Limited.',
    publisher_address: 'Hill View Colony, Near Chandra Vatika, Khatima, Udham Singh Nagar - 262308, Uttarakhand, India.',
    publisher_email: 'publications@domusmediverse.com',
    publisher_phone: '+91 98765 43210'
  },

  editorial_board: {
    overview: '',
    board_policy: '',
    dheeraj_intro: ''
  },

  // Home page: the three cards below the editorial board (Admin -> Live Page Contents -> Home Page Cards).
  // Bodies are HTML.
  home_cards: {
    card1_heading: 'About AJHM',
    card1_body: '<p>To publish high-quality, peer-reviewed clinical, pharmacological, and philosophical scientific research that contributes to the advancement of homoeopathy, patient safety, and evidence-informed integrative healthcare globally.</p>',
    card2_heading: 'Aims & Scope',
    card2_body: '<p>To provide an authoritative, peer-reviewed, open-access international platform dedicated to publishing high-quality, methodologically sound scientific research in homoeopathic medicine and allied integrative healthcare disciplines.</p>',
    card3_heading: 'DOMUS Publications',
    card3_body: '<p>An academic publishing imprint of Domus Mediverse Private Limited.</p>\n<p><strong>Registered Office:</strong><br>C/O Dr Dheeraj Pandey, Hill View Colony, Near Chandra Vatika, Khatima, Udham Singh Nagar - 262308, Uttarakhand, India</p>'
  },

  archives: {
    overview: '',
    frequency: 'Biannual (Issue 1: January–June; Issue 2: July–December)',
    preservation_statement: ''
  }
};

// Pages written as HTML (src/data/pageHtml.js) default to Simple HTML mode
Object.entries(PAGE_HTML).forEach(([pageKey, html]) => {
  DEFAULT_PAGE_CONTENTS[pageKey] = {
    ...DEFAULT_PAGE_CONTENTS[pageKey],
    use_raw_html: true,
    full_content: html,
  };
});

// Site path of each editable page (first entry is the main path, the rest are route aliases)
export const PAGE_PATHS = {
  about_us: ['/about-us', '/about'],
  aims_and_scope: ['/aims-and-scope', '/aims-scope'],
  author_guidelines: ['/information-for-authors', '/author-guidelines'],
  peer_review: ['/peer-review', '/submission-guidelines'],
  publication_ethics: ['/publication-ethics', '/ethics'],
  contact: ['/contact'],
  editorial_board: ['/editorial-board'],
  archives: ['/archives'],
  call_for_papers: ['/call-for-papers'],
};

const plainText = (html) =>
  typeof html === 'string' ? html.replace(/<[^>]*>?/gm, ' ').replace(/\s+/g, ' ').trim() : '';

/**
 * Publisher details for the Home and article pages, from Admin -> Live Page Contents -> About the Journal
 * (Publisher Name, Registered Address). "Domus Publications, an academic publishing imprint of ..." is split
 * into the name and the line below it.
 */
export function getPublisherInfo(aboutContent) {
  const defaults = DEFAULT_PAGE_CONTENTS.about_us;
  const fullName = (plainText(aboutContent?.publisher_name) || defaults.publisher_name).replace(/\.$/, '');
  const commaAt = fullName.indexOf(',');
  return {
    name: commaAt === -1 ? fullName : fullName.slice(0, commaAt).trim(),
    imprint: commaAt === -1 ? '' : fullName.slice(commaAt + 1).trim(),
    address: (plainText(aboutContent?.publisher_address) || defaults.publisher_address).replace(/\.$/, ''),
  };
}

export const PAGE_CONTENT_UPDATED_EVENT = 'page_content_updated';

const notifyPageContentUpdated = (pageKey) => {
  try {
    window.dispatchEvent(new CustomEvent(PAGE_CONTENT_UPDATED_EVENT, { detail: { pageKey } }));
  } catch (e) {
    // Ignore (non-browser environment)
  }
};

// Last saved pages read in this visit, so pages opened later show the saved text straight away
let savedPagesCache = null;

const mergeSaved = (pageKey, saved) => ({ ...DEFAULT_PAGE_CONTENTS[pageKey], ...(saved?.[pageKey] || {}) });

/**
 * Page content: the defaults above plus whatever the admin panel saved
 */
export async function getPageContent(pageKey) {
  const saved = await loadData('pages', {});
  savedPagesCache = saved || {};
  return mergeSaved(pageKey, saved);
}

/**
 * Save one page's content
 */
export async function savePageContent(pageKey, content) {
  const saved = await loadData('pages', {});
  const updated = { ...(saved || {}), [pageKey]: { ...content, updatedAt: new Date().toISOString() } };
  await saveToCode('pages', updated);
  savedPagesCache = updated;
  notifyPageContentUpdated(pageKey);
  return true;
}

/**
 * Custom React Hook to load and watch page content.
 * `loading` stays true until the saved text has arrived the first time in this visit, so pages can
 * wait instead of flashing the built-in default text (and its headings) first.
 */
export function usePageContent(pageKey) {
  const [content, setContent] = useState(() =>
    savedPagesCache ? mergeSaved(pageKey, savedPagesCache) : DEFAULT_PAGE_CONTENTS[pageKey] || {}
  );
  const [loading, setLoading] = useState(!savedPagesCache);
  const [error, setError] = useState(null);

  const fetchContent = useCallback(async () => {
    try {
      const data = await getPageContent(pageKey);
      setContent(data);
      setError(null);
    } catch (err) {
      setError(err);
    }
    setLoading(false);
  }, [pageKey]);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  return { content, loading, error, refresh: fetchContent };
}
